import React, { useState, useEffect } from 'react';
import {
  MenuItem,
  MenuCategory,
  MenuItemImage,
  MenuItemStatus,
  MENU_ITEM_STATUS_LABELS,
} from '../types/api';
import { partnerApiService } from '../services/api';
import { categoryPathLabel, leafCategories } from '../utils/categoryTree';
import { CURRENCY } from '../config/constants';
import { FormShell, FormCard, FormFoot } from './form/FormShell';
import { Field, FieldGrid, Suffixed } from './form/Field';
import { Toast, useToast } from './form/Toast';

interface MenuItemFormProps {
  item?: MenuItem;
  categories: MenuCategory[];
  initialCategoryId?: number;
  onSave: () => void;
  onCancel: () => void;
}

const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

export function MenuItemForm({
  item,
  categories,
  initialCategoryId,
  onSave,
  onCancel,
}: MenuItemFormProps) {
  const [formData, setFormData] = useState<{
    name: string;
    price: string;
    description: string;
    status: MenuItemStatus;
    menu_category_id: number;
  }>({
    name: '',
    price: '',
    description: '',
    status: 'active',
    menu_category_id: initialCategoryId || 0,
  });

  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [existingImages, setExistingImages] = useState<MenuItemImage[]>([]);
  const [imagesToDelete, setImagesToDelete] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useToast();

  const isEdit = !!item;

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name,
        price: item.price.toString(),
        description: item.description ?? '',
        status: item.status ?? 'active',
        menu_category_id: item.menu_category_id,
      });
      setExistingImages(item.images);
      setImagePreviews(item.images.map((img) => img.url));
    } else if (categories.length > 0 && !formData.menu_category_id) {
      // Товары кладутся только в конечные разделы: раздел с подразделами в
      // списке не появится, и подставлять его по умолчанию нельзя.
      const fallback = leafCategories(categories)[0]?.id ?? 0;
      setFormData((prev) => ({ ...prev, menu_category_id: initialCategoryId || fallback }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, categories, initialCategoryId]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setIsDirty(true);
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Сбрасываем input, иначе повторный выбор того же файла не вызовет change.
    e.target.value = '';
    if (!file) return;

    if (file.size > MAX_IMAGE_BYTES) {
      setToast({ message: 'Файл слишком большой. Максимум 2 МБ', tone: 'error' });
      return;
    }

    if (images.length > 0 || existingImages.length > 0) {
      if (!window.confirm('Заменить текущее изображение?')) return;
      // Замену разбирает бэкенд по новому файлу — images_to_delete здесь
      // намеренно очищается, поведение сохранено с прошлой версии формы.
      setExistingImages([]);
      setImagesToDelete([]);
    }

    setImages([file]);
    setIsDirty(true);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreviews([ev.target?.result as string]);
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = (index: number) => {
    if (index < existingImages.length) {
      const img = existingImages[index];
      setImagesToDelete((prev) => [...prev, img.id]);
      setExistingImages((prev) => prev.filter((_, i) => i !== index));
    } else {
      const newIndex = index - existingImages.length;
      setImages((prev) => prev.filter((_, i) => i !== newIndex));
    }
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Введите название позиции';
    if (!formData.price || parseFloat(formData.price) <= 0) errors.price = 'Введите корректную цену';
    if (!formData.menu_category_id) errors.menu_category_id = 'Выберите категорию';
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setToast({ message: 'Заполните обязательные поля', tone: 'error' });
      return;
    }
    setLoading(true);
    try {
      const submitData = {
        name: formData.name.trim(),
        menu_category_id: Number(formData.menu_category_id),
        price: parseFloat(formData.price),
        description: formData.description.trim(),
        status: formData.status,
        images: images.length > 0 ? images : undefined,
        images_to_delete: imagesToDelete.length > 0 ? imagesToDelete : undefined,
      };
      if (isEdit) {
        await partnerApiService.updateMenuItem(item.id, submitData);
      } else {
        await partnerApiService.createMenuItem(submitData);
      }
      setIsDirty(false);
      setToast({ message: isEdit ? 'Позиция обновлена' : 'Позиция создана', tone: 'success' });
      setTimeout(() => onSave(), 800);
    } catch (error: any) {
      console.error('Ошибка сохранения позиции:', error);
      const msg = error.response?.data?.message || 'Не удалось сохранить позицию';
      setToast({ message: msg, tone: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const selectedCategory = categories.find((c) => c.id === Number(formData.menu_category_id));
  // В подписи — весь путь: «Чаи» встречается и в баре, и в магазине.
  const categoryName = selectedCategory ? categoryPathLabel(selectedCategory, categories) : undefined;
  const saveLabel = loading ? 'Сохранение…' : isEdit ? 'Сохранить' : 'Создать';
  const preview = imagePreviews[0];

  return (
    <>
      <FormShell
        eyebrow={`Каталог${categoryName ? ` · ${categoryName}` : ''}`}
        title={isEdit ? 'Редактирование позиции' : 'Новая позиция'}
        isDirty={isDirty}
        actions={
          <>
            <button type="button" className="qp-btn qp-btn--outline" onClick={onCancel} disabled={loading}>
              Отменить
            </button>
            <button type="submit" form="menu-item-form" className="qp-btn qp-btn--primary" disabled={loading}>
              {saveLabel}
            </button>
          </>
        }
        footer={
          <FormFoot note="Позиция появится в приложении сразу после сохранения — вместе с ценой и фото.">
            <button type="submit" form="menu-item-form" className="qp-btn qp-btn--primary" disabled={loading}>
              {saveLabel}
            </button>
          </FormFoot>
        }
      >
        <FormCard
          title="Позиция меню"
          hint={
            isEdit
              ? 'Название, категория и цена — то, что гость видит в карточке заведения.'
              : 'Заполните название и цену: позже позицию можно будет включить в скидку.'
          }
        >
          <form id="menu-item-form" onSubmit={handleSubmit} noValidate>
            <FieldGrid>
              <Field label="Название позиции" required wide htmlFor="menu-item-name" error={validationErrors.name}>
                <input
                  id="menu-item-name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="qp-control"
                  placeholder="Например: Паста карбонара"
                  disabled={loading}
                />
              </Field>

              <Field
                label="Категория"
                required
                htmlFor="menu-item-category"
                error={validationErrors.menu_category_id}
              >
                <select
                  id="menu-item-category"
                  name="menu_category_id"
                  value={formData.menu_category_id}
                  onChange={handleInputChange}
                  className="qp-control"
                  disabled={loading}
                >
                  <option value={0}>Выберите раздел</option>
                  {leafCategories(categories).map((category) => (
                    <option key={category.id} value={category.id}>
                      {categoryPathLabel(category, categories)}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Цена" required htmlFor="menu-item-price" error={validationErrors.price}>
                <Suffixed suffix={CURRENCY.symbol}>
                  <input
                    id="menu-item-price"
                    name="price"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={handleInputChange}
                    className="qp-control qp-control--num"
                    placeholder="0"
                    disabled={loading}
                  />
                </Suffixed>
              </Field>

              <Field
                label="На витрине"
                htmlFor="menu-item-status"
                hint={
                  formData.status === 'active'
                    ? 'Позиция видна в приложении.'
                    : 'Позиция остаётся в каталоге, но в приложении её не видно.'
                }
              >
                <select
                  id="menu-item-status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="qp-control"
                  disabled={loading}
                >
                  {(Object.keys(MENU_ITEM_STATUS_LABELS) as MenuItemStatus[]).map((value) => (
                    <option key={value} value={value}>
                      {MENU_ITEM_STATUS_LABELS[value]}
                    </option>
                  ))}
                </select>
              </Field>

              <Field
                label="Описание"
                wide
                htmlFor="menu-item-description"
                hint="Состав, вес, объём порции. Гость видит эту строку под названием — цену и скидку сюда дублировать не нужно."
              >
                <textarea
                  id="menu-item-description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="qp-control"
                  placeholder="Например: спагетти, бекон, пармезан, желток — 320 г"
                  disabled={loading}
                />
              </Field>

              <Field label="Фото" wide hint="PNG или JPG до 2 МБ. Лучше горизонтальное — так позиция ровно ложится в список.">
                {preview ? (
                  <div className="qp-shot qp-shot--cover">
                    <img src={preview} alt="Фото позиции" />
                    <div className="qp-shot-actions">
                      <label className="qp-shot-btn" htmlFor="menu-item-image-input">
                        Заменить
                      </label>
                      <button
                        type="button"
                        className="qp-shot-btn qp-shot-btn--icon"
                        onClick={() => handleRemoveImage(0)}
                        aria-label="Удалить фото"
                        disabled={loading}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
                          <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ) : (
                  <label className="qp-drop" htmlFor="menu-item-image-input">
                    <span className="qp-drop-label">Перетащите файл или</span>
                    <span className="qp-btn qp-btn--outline">Выбрать файл</span>
                  </label>
                )}
                <input
                  id="menu-item-image-input"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  disabled={loading}
                  style={{ display: 'none' }}
                />
              </Field>
            </FieldGrid>
          </form>
        </FormCard>
      </FormShell>

      <Toast toast={toast} />
    </>
  );
}
